import useSWR, { type BareFetcher, type SWRConfiguration } from 'swr'

import { type ApiExperimentResponse } from '@drill-test/client'
import {
  getDrillTestEnableStatus,
  enableDrillTest,
  disableDrillTest,
  getChaosExperimentsList,
  createChaosExperiment,
  deleteChaosExperiment,
  startChaosExperiment,
  getChaosExperimentDetail,
  getExperimentActionsList,
  approveExperiment,
  updateExperimentInfo
} from '~/api'
import { useMutation } from '~/hooks/use-mutation'

const DRILL_TEST_ENABLE_STATUS_CACHE_KEY = 'drill-test-enable-status'
const EXPERIMENT_LIST_CACHE_KEY = 'experiment-list'
const EXPERIMENT_DETAIL_CACHE_KEY = 'experiment-detail'

export function useGetDrillTestEnableStatus({
  oid,
  cid
}: {
  oid: string
  cid: string
}) {
  const { data, error, mutate } = useSWR(
    {
      cacheId: DRILL_TEST_ENABLE_STATUS_CACHE_KEY,
      oid,
      cid
    },
    () => getDrillTestEnableStatus({ oid, cid })
  )
  return {
    data: data,
    isLoading: !error && !data,
    isError: !!error,
    mutate
  }
}

/**
 * Hook for updating a drill test enable status
 */
export function useEnableDrillTest() {
  const { run: mutate, ...mutation } = useMutation(enableDrillTest)

  return {
    ...mutation,
    mutate
  }
}

/**
 * Hook for deleting a drill test enable status
 */
export function useDisableDrillTest() {
  const { run: mutate, ...mutation } = useMutation(disableDrillTest)

  return {
    ...mutation,
    mutate
  }
}

export function useGetExperimentList(
  {
    oid,
    cid,
    page,
    size
  }: {
    oid: string
    cid: string
    page?: number
    size?: number
  },
  config?: SWRConfiguration<
    ApiExperimentResponse[] | undefined,
    any,
    BareFetcher<ApiExperimentResponse[] | undefined>
  >
) {
  const { data, error, mutate } = useSWR(
    {
      cacheId: EXPERIMENT_LIST_CACHE_KEY,
      oid,
      cid
    },
    () => getChaosExperimentsList({ oid, cid, page, size })
    // { refreshInterval: 2000 }
  )

  return {
    data: data,
    isLoading: !error && !data,
    isError: !!error,
    mutate
  }
}

export function useGetExperimentActionsList() {
  const { run: mutate, ...mutation } = useMutation(getExperimentActionsList)
  return {
    ...mutation,
    mutate
  }
}

export function useCreateExperiment() {
  const { run: mutate, ...mutation } = useMutation(createChaosExperiment)

  return {
    ...mutation,
    mutate
  }
}

export function useDeleteExperiment() {
  const { run: mutate, ...mutation } = useMutation(deleteChaosExperiment)

  return {
    ...mutation,
    mutate
  }
}

export function useStartExperiment() {
  const { run: mutate, ...mutation } = useMutation(startChaosExperiment)

  return {
    ...mutation,
    mutate
  }
}

export function useGetChaosExperimentDetail({
  oid,
  cid,
  eid
}: {
  oid: string
  cid: string
  eid: string
}) {
  const { data, error, mutate } = useSWR(
    {
      cacheId: EXPERIMENT_DETAIL_CACHE_KEY,
      oid,
      cid,
      eid
    },
    () => getChaosExperimentDetail({ oid, cid, eid })
  )

  return {
    data: data,
    isLoading: !error && !data,
    isError: !!error,
    mutate
  }
}

export function useApproveExperiment() {
  const { run: mutate, ...mutation } = useMutation(approveExperiment)

  return {
    ...mutation,
    mutate
  }
}

export function useUpdateExperimentInfo() {
  const { run: mutate, ...mutation } = useMutation(updateExperimentInfo)

  return {
    ...mutation,
    mutate
  }
}
